import React, { useEffect, useState, useCallback } from 'react';

import { Backdrop, Box, CircularProgress, IconButton, Portal, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  image: {
    display: 'block',
    maxWidth: 'calc(90vw - 96px)',
    maxHeight: 'calc(90vh - 96px)',
    objectFit: 'contain',
    userSelect: 'none',
    pointerEvents: 'none',
    WebkitUserDrag: 'none',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: '#fff',
    zIndex: 1,
  },
  loadingContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#fff',
  },
  navButtonLeft: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
    zIndex: 1,
  },
  navButtonRight: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
    zIndex: 1,
  },
}));

interface ViewImageDialogProps {
  srcs: string[];
  initialIndex: number;
  onClose: () => void;
}

const ViewImageDialog = ({ srcs, initialIndex, onClose }: ViewImageDialogProps) => {
  const classes = useStyles();
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);

  const handlePrev = useCallback(() => {
    if (srcs.length <= 1) return;
    setActiveIndex(prev => {
      if (prev === 0) return prev;
      setLoading(true);
      return prev - 1;
    });
  }, [srcs.length]);

  const handleNext = useCallback(() => {
    if (srcs.length <= 1) return;
    setActiveIndex(prev => {
      if (prev === srcs.length - 1) return prev;
      setLoading(true);
      return prev + 1;
    });
  }, [srcs.length]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, handlePrev, handleNext]);

  return (
    <Portal>
      <Backdrop
        open
        className={classes.backdrop}
        onClick={event => {
          onClose();
          event.stopPropagation();
        }}
      >
        <IconButton
          aria-label="Close image viewer"
          className={classes.closeButton}
          onClick={event => {
            event.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon />
        </IconButton>

        {srcs.length > 1 && activeIndex > 0 && (
          <IconButton
            aria-label="Previous image"
            className={classes.navButtonLeft}
            onClick={event => {
              event.stopPropagation();
              handlePrev();
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        )}

        {srcs.length > 1 && activeIndex < srcs.length - 1 && (
          <IconButton
            aria-label="Next image"
            className={classes.navButtonRight}
            onClick={event => {
              event.stopPropagation();
              handleNext();
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        )}

        <Box sx={{ position: 'relative', p: 6 }} onClick={event => event.stopPropagation()}>
          {loading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress color="inherit" />
            </Box>
          )}
          <img
            src={srcs[activeIndex]}
            className={classes.image}
            style={{ display: loading ? 'none' : 'block' }}
            alt={`${activeIndex + 1} of ${srcs.length}`}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          />
        </Box>
      </Backdrop>
    </Portal>
  );
};

export default ViewImageDialog;
